import type { Injector } from '@angular/core';
import { inject, runInInjectionContext } from '@angular/core';
import {
  type FormControl,
  FormControlDirective,
  FormControlName,
  NgControl,
  NgModel,
} from '@angular/forms';

/**
 * Injects and retrieves a `FormControl` and its associated `NgControl` from the provided `Injector`.
 *
 * @param {Injector} injector - The injector to use for retrieving the `NgControl`.
 * @return {{ control: FormControl<T>, ngControl: NgControl }} - An object containing the `FormControl` and its associated `NgControl`.
 * @throws {Error} Throws an error if no `FormControl` can be found.
 */
export function injectFormControl<T>(injector: Injector): {
  control: FormControl<T>;
  ngControl: NgControl;
} {
  const ngControl = runInInjectionContext(injector, () =>
    inject(NgControl, { self: true, host: true }),
  );

  let control: FormControl<T> | undefined = undefined;

  if (ngControl instanceof FormControlDirective) {
    control = ngControl.control;
  } else if (ngControl instanceof FormControlName) {
    control = ngControl.formDirective.control.get(ngControl.path);
  } else if (ngControl instanceof NgModel) {
    control = ngControl.control;
  }

  if (control === undefined) {
    throw new Error('[injectFormControl] not found FormControl');
  } else {
    return { control, ngControl };
  }
}
